import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BiCheck, BiChevronDown, BiChevronRight, BiFile, BiFolder, BiGridAlt } from 'react-icons/bi';
import { QuestionType } from '../../core/constants/common';

// Mirrors PreviewTreeNavigation's ancestor lookup so the current Section/Subsection
// stays expanded and visible in the tree as the active section changes.
const findParentSectionId = (sectionId, sections) => {
  for (const section of sections || []) {
    if (section.subSections?.some((s) => s.id === sectionId)) return section.id;
    const parentId = findParentSectionId(sectionId, section.subSections);
    if (parentId != null) return parentId;
  }
  return null;
};

// Same rule FormSidebar's isFieldAnswered used, kept identical so the green
// "answered" indicator reflects the last-saved submittedData exactly as before.
const isFieldAnswered = (field, submission) => {
  // A GRID question owns no response of its own (its entry is stripped on save);
  // its cells are answered via the linked fields' responses, so it counts as
  // answered once any linked cell has a value.
  if (field.type === QuestionType.GRID) {
    const linkedIds = (field.gridQuestionConfig?.cellLinks || [])
      .map((link) => link.linkedFieldId)
      .filter(Boolean);
    return linkedIds.some((id) => {
      const linked = submission?.responses?.find((d) => d.formField.id === id);
      return !!linked
        && ((linked.textResponse !== null && linked.textResponse !== undefined && linked.textResponse !== '')
          || (linked.numericResponse !== null && linked.numericResponse !== undefined && linked.numericResponse !== ''));
    });
  }

  const response = submission?.responses?.find((d) => d.formField.id === field.id);
  if (!response) return false;

  if (field.type === QuestionType.SINGLE_TEXTBOX || field.type === QuestionType.TEXT_BOX) {
    return response.textResponse !== null && response.textResponse !== '';
  }
  if (field.type === QuestionType.NUMERIC_VALUE) {
    return response.numericResponse !== null;
  }
  if (field.type === QuestionType.DROP_DOWN
    || field.type === QuestionType.RADIO
    || field.type === QuestionType.MULTIPLE_CHOICE
    || field.type === QuestionType.MULTI_SELECT_DROP_DOWN) {
    return response.responseOptions?.length > 0;
  }
  if (field.type === QuestionType.DATE_TIME_BOX) {
    return response.dateResponse !== null;
  }
  if (field.type === QuestionType.DATE_RANGE) {
    return response.dateResponse !== null || response.toDateResponse !== null;
  }
  if (field.type === QuestionType.FILE_UPLOAD) {
    return response.files !== null;
  }
  if (field.type === QuestionType.TABLE) {
    return response.tableRowResponses?.length > 0;
  }
  if (field.type === QuestionType.RADIO_GRID) {
    return response.gridResponses?.length > 0;
  }
  return false;
};

// Ported verbatim from FormSidebar's iterateSectionsAndSetAnsweredData: a Section
// is "answered" once every one of its own fields is answered, and that status
// propagates up so a parent Section is only marked answered when it and all of
// its Subsections are.
const iterateSectionsAndSetAnsweredData = ({ parentSection, sections, submission, data }) => {
  sections?.forEach((section) => {
    let answeredCount = 0;
    section.fields?.forEach((field) => {
      if (isFieldAnswered(field, submission)) {
        answeredCount++;
        data.field[field.id] = true;
      } else {
        data.field[field.id] = false;
      }
    });
    if (section.subSections?.length > 0) {
      iterateSectionsAndSetAnsweredData({
        parentSection: { section, fieldsAnswered: answeredCount === section.fields?.length },
        sections: section.subSections,
        submission,
        data,
      });
    } else {
      data.section[section.id] = answeredCount === section.fields?.length;
    }
    if (parentSection) {
      data.section[parentSection.section.id] = parentSection.fieldsAnswered && data.section[section.id];
    }
  });
};

const computeAnsweredData = (fields, sections, submission) => {
  const data = { section: {}, field: {} };
  fields?.forEach((field) => {
    data.field[field.id] = isFieldAnswered(field, submission);
  });
  iterateSectionsAndSetAnsweredData({ parentSection: null, sections, submission, data });
  return data;
};

const checkValidationError = (fieldId, validationError) => (
  !!fieldId && validationError?.length > 0 && validationError.includes(fieldId)
);

// Ported from FormSidebar's sectionHasValidationErrors: checks the Section's own
// fields plus one level of Subsection fields, same depth the original checked.
const sectionHasValidationErrors = (section, validationError) => {
  if (!validationError?.length) return false;

  for (let i = 0; i < section?.fields?.length; i++) {
    const field = section.fields[i];
    if (field && validationError.includes(field.id)) return true;
  }
  for (let i = 0; i < section?.subSections?.length; i++) {
    for (let j = 0; j < section.subSections[i]?.fields?.length; j++) {
      const field = section.subSections[i].fields[j];
      if (field && validationError.includes(field.id)) return true;
    }
  }
  return false;
};

const FormSubmitTreeNavigation = ({
  name,
  fields,
  sections,
  currentSectionId,
  submittedData,
  validationError,
  onSelectRootFields,
  onSelectSection,
  onSelectSubSection,
  onSelectQuestion,
}) => {
  const [expanded, setExpanded] = useState({ 'root-fields': true });
  const [searchQuery, setSearchQuery] = useState('');
  const treeBodyRef = useRef(null);

  const answeredData = useMemo(
    () => computeAnsweredData(fields, sections, submittedData),
    [fields, sections, submittedData]
  );

  const toggleExpand = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Auto-expand ancestor sections/subsections of whichever Section is currently
  // active, so it stays visible in the tree — same behavior as PreviewTreeNavigation.
  useEffect(() => {
    if (currentSectionId == null || currentSectionId === 0) return;

    const ancestorIds = [];
    let curId = currentSectionId;
    while (curId != null) {
      ancestorIds.push(curId);
      curId = findParentSectionId(curId, sections);
    }

    setExpanded((prev) => {
      let changed = false;
      const next = { ...prev };
      ancestorIds.forEach((id) => {
        const key = `section-${id}`;
        if (next[key] === false) {
          next[key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [currentSectionId, sections]);

  useEffect(() => {
    const container = treeBodyRef.current;
    if (!container || currentSectionId == null) return;

    const target = container.querySelector(`[data-tree-section-id="${currentSectionId}"]`);
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [currentSectionId, expanded]);

  // Same live-filter rules PreviewTreeNavigation uses: an empty query matches
  // everything, otherwise a case-insensitive substring match against the name.
  const matchesSearch = (text) => {
    if (!searchQuery) return true;
    return text?.toLowerCase().includes(searchQuery.toLowerCase());
  };

  // A Section/Subsection stays visible while searching if its own title matches,
  // one of its own fields matches, or any of its (sub)sections matches recursively.
  const sectionMatchesSearch = (section) => {
    if (matchesSearch(section.title)) return true;
    if (section.fields?.some((f) => matchesSearch(f.name))) return true;
    return section.subSections?.some((sub) => sectionMatchesSearch(sub));
  };

  const renderQuestionRow = (field, depth, onClick) => (
    <div
      key={field.id}
      role="treeitem"
      data-tree-field-id={field.id}
      className={`builder-tree__item builder-tree__item--preview-field Preview-Question-field builder-tree__item--nested${depth > 0 ? '-2' : ''} ${checkValidationError(field.id, validationError) ? 'builder-tree__item--invalid' : ''}`}
      onClick={onClick}
    >
      <BiFile className="builder-tree__icon" aria-hidden="true" />
      <span className="builder-tree__label">{field.name || 'Untitled'}</span>
      {answeredData.field[field.id] === true && <BiCheck className="builder-tree__answered" aria-hidden="true" />}
    </div>
  );

  const renderSection = (section, depth = 0, parentSubSections = null) => {
    if (section.hide_alt || !sectionMatchesSearch(section)) return null;

    const key = `section-${section.id}`;
    const isExpanded = expanded[key] !== false;
    const isContainerActive = currentSectionId === section.id;
    const isAnswered = answeredData.section[section.id] === true;
    const hasError = sectionHasValidationErrors(section, validationError);
    const visibleFields = section.fields?.filter((f) => !f.hide_alt && matchesSearch(f.name)) ?? [];
    const visibleSubSections = section.subSections?.filter((s) => !s.hide_alt && sectionMatchesSearch(s)) ?? [];
    const hasChildren = visibleSubSections.length > 0 || visibleFields.length > 0;

    const handleSelect = () => {
      if (depth > 0 && parentSubSections) {
        onSelectSubSection(section.id, parentSubSections);
      } else {
        onSelectSection(section.id);
      }
    };

    const handleFieldClick = (field) => {
      if (!isContainerActive) handleSelect();
      onSelectQuestion?.(field.id);
    };

    return (
      <div key={section.id} role="group">
        <div className="builder-tree__section-row">
          <div
            role="treeitem"
            aria-expanded={hasChildren ? isExpanded : undefined}
            aria-selected={isContainerActive}
            data-tree-section-id={section.id}
            className={`builder-tree__item ${depth > 0 ? 'builder-tree__item--nested' : ''} ${isContainerActive ? 'builder-tree__item--selected' : ''} ${hasError ? 'builder-tree__item--invalid' : ''}`}
            onClick={handleSelect}
          >
            {hasChildren ? (
              <button
                type="button"
                className="builder-ui-btn builder-ui-btn--ghost builder-ui-btn--icon"
                style={{ width: 24, height: 24, padding: 0 }}
                onClick={(e) => { e.stopPropagation(); toggleExpand(key); }}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? <BiChevronDown /> : <BiChevronRight />}
              </button>
            ) : (
              <span style={{ width: 24, height: 24, display: 'inline-block', flexShrink: 0 }} />
            )}
            <BiFolder className="builder-tree__icon" aria-hidden="true" />
            <span className="builder-tree__label">{section.title}</span>
            {isAnswered && <BiCheck className="builder-tree__answered" aria-hidden="true" />}
          </div>
        </div>
        {isExpanded && (
          <>
            {visibleFields.map((field) => renderQuestionRow(field, depth, () => handleFieldClick(field)))}
            {visibleSubSections.map((sub) => renderSection(sub, depth + 1, section.subSections))}
          </>
        )}
      </div>
    );
  };

  const isRootSelected = currentSectionId === 0 || currentSectionId == null;
  const rootKey = 'root-fields';
  const isRootExpanded = expanded[rootKey] !== false;
  const allRootFields = fields?.filter((f) => !f.hide_alt) ?? [];
  const visibleRootFields = allRootFields.filter((f) => matchesSearch(f.name));

  const handleRootFieldClick = (field) => {
    if (!isRootSelected) onSelectRootFields(field.id);
    onSelectQuestion?.(field.id);
  };

  return (
    <nav className="builder-tree builder-tree--preview" aria-label="Form structure">
      <div className="builder-tree__header">
        <div className="builder-tree__search">
          <input
            type="search"
            placeholder="Filter tree…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Filter navigation tree"
          />
        </div>
      </div>
      <div className="builder-tree__body" role="tree" ref={treeBodyRef}>
        <div role="group">
          <div
            role="treeitem"
            aria-expanded={allRootFields.length > 0 ? isRootExpanded : undefined}
            aria-selected={isRootSelected}
            data-tree-section-id={0}
            className={`builder-tree__item ${isRootSelected ? 'builder-tree__item--selected' : ''}`}
            onClick={() => onSelectRootFields(fields?.[0]?.id)}
          >
            {allRootFields.length > 0 ? (
              <button
                type="button"
                className="builder-ui-btn builder-ui-btn--ghost builder-ui-btn--icon"
                style={{ width: 24, height: 24, padding: 0 }}
                onClick={(e) => { e.stopPropagation(); toggleExpand(rootKey); }}
                aria-label={isRootExpanded ? 'Collapse' : 'Expand'}
              >
                {isRootExpanded ? <BiChevronDown /> : <BiChevronRight />}
              </button>
            ) : (
              <span style={{ width: 24, height: 24, display: 'inline-block', flexShrink: 0 }} />
            )}
            <BiGridAlt className="builder-tree__icon" aria-hidden="true" />
            <span className="builder-tree__label">{name}</span>
          </div>
          {isRootExpanded && visibleRootFields.length > 0 && visibleRootFields.map((field) => renderQuestionRow(field, 0, () => handleRootFieldClick(field)))}
        </div>
        {sections?.filter((s) => !s.hide_alt).map((section) => renderSection(section))}
      </div>
    </nav>
  );
};

export default FormSubmitTreeNavigation;
